import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { DialogTitle, DialogContent, DialogActions, Dialog, Button } from '@mui/material';
import { format } from 'date-fns-tz';
import AsStandInLabel from '@/components/UI/AsStandInLabel/AsStandInLabel';
import { DAY_DATE_FORMAT, CREATOR_CR, STAND_IN_CR, REQUESTER_CR, INFORMER, ADMIN_CR, statusSubCampaign, statusCampaign, APPROVER_CR } from '@/helpers/constants';
import { isCampaignRDMP } from '@/helpers/utils';
import useAuth from '@/hooks/useAuth';
import { getCampaignInfo } from '@/redux/Campaign/campaign.reducer';
import { CampaignInfo, Comment, Step } from '@/types';
import styles from './DialogStatus.module.scss';
import InputText from '../UI/InputText/InputText';

interface DialogStatusProps {
  close: () => void;
  title: string;
  data: Step;
  headerLanguage: string;
  campaignInfo: CampaignInfo;
  getActionTakenLabel: (actionTaken: string, statusCode: string) => string;
  isVANCR?: boolean;
}

const COMMENT_LABELS = {
  CLOSED: 'closed',
  IN_RD_COMMITTEE: 'in_rd_committee',
  IN_CAUSER_APPROVAL: 'in_causer_approval',
  IN_INFORMER_APPROVAL: 'in_informer_approval',
};

const DialogStatus: React.FC<DialogStatusProps> = (props) => {
  const { close, title, data, headerLanguage, campaignInfo, getActionTakenLabel, isVANCR } = props;
  const { t } = useTranslation();
  const authService = useAuth();

  const currentRole = campaignInfo.authorization.campaignRole;
  const approverRegEx = /[_]/;

  const approverType = (): string => {
    if (currentRole === 'NONE' && campaignInfo.authorization.kindOfPermission === ADMIN_CR) return ADMIN_CR;
    if (currentRole === STAND_IN_CR) return STAND_IN_CR;

    return currentRole.split(approverRegEx)[1] || currentRole;
  };

  const matchedRecipient = Object.entries(campaignInfo.recipients)
    .find(([key]) => key === authService.userCode)?.[1];

  const isInformer = matchedRecipient?.includes(INFORMER);

  const displayComment = (role: string): boolean => [role, APPROVER_CR, CREATOR_CR, STAND_IN_CR, REQUESTER_CR, ADMIN_CR].includes(approverType())
    && !!matchedRecipient;

  const commentsByLabel = useMemo(() => {
    return data?.comments?.reduce((acc, comment) => {
      acc[comment.commentLabel] = comment;

      return acc;
    }, {} as Record<string, Comment>) || {};
  }, [data?.comments]);

  // REMOVE WHEN WORKFLOW IS FIXED
  const isBlockedByChangeRequest = data.actionTaken === statusSubCampaign.REQUESTED_FOR_CHANGES
    && data.statusCode === statusSubCampaign.IN_PLANT_VALIDATION;
  const getTitle = isBlockedByChangeRequest ? t('subCampaign.BLOCKED_BY_CHANGE_REQUEST') : title;
  // REMOVE WHEN WORKFLOW IS FIXED

  return (
    <Dialog
      open
      maxWidth="md"
      onClose={close}
      aria-labelledby="form-dialog-status"
    >
      <DialogTitle id="form-dialog-status-title">{getTitle}</DialogTitle>
      <DialogContent>
        {data?.statusCode === 'IN_CREATION' && <h3>{`${t('titles.campaign')} - ${getActionTakenLabel(data?.actionTaken, data?.statusCode)}`}</h3>}
        {(['IN_CAUSER_APPROVAL', 'IN_INFORMER_APPROVAL', 'IN_CAUSER_PRE_APPROVAL', 'IN_INFORMER_PRE_APPROVAL',
          'IN_CAUSER_CHECK', 'IN_INFORMER_CHECK'].includes(data?.statusCode))
        && <h3>{t('dialog.statusApprovalDone')}</h3>}
        <div className={styles.DialogStatus}>
          <div className={styles.Flex}>
            <div className={data?.userAssignedByAction?.code ? styles.TwoColumns : styles.OneColumns}>
              <InputText
                disabled
                id="input-status-action-taken-by"
                label={t('dialog.statusActionTakenBy')}
                value={data?.actionTakenBy?.summary}
                startAdornment={data?.approvedByStandin && <AsStandInLabel isInput />}
              />
            </div>
            {data?.userAssignedByAction?.code && (
              <div className={styles.TwoColumns}>
                <InputText
                  disabled
                  id="input-status-asigned-user"
                  label={t('dialog.statusAsignedUser')}
                  value={data?.userAssignedByAction?.summary}
                />
              </div>
            )}
          </div>
          {data?.statusCode === 'IN_CREATION' && (
            <>
              <div className={styles.Flex}>
                <div className={styles.TwoColumns}>
                  <InputText
                    disabled
                    id="input-status-version"
                    label={t('dialog.statusVersion')}
                    value={data?.version}
                  />
                </div>
                <div className={styles.TwoColumns}>
                  <InputText
                    disabled
                    id="input-status-created"
                    label={t('dialog.statusTimestamp')}
                    value={format(new Date(data?.actionDate), DAY_DATE_FORMAT)}
                  />
                </div>
              </div>
              {data?.actionLedToNonSuccessfulStatus && (
                <div className={styles.OneColumns}>
                  <InputText
                    disabled
                    id="input-status-comment-for-approvers"
                    label={t('dialog.statusCommentForApprovers')}
                    multiline
                    rows={4}
                    value={commentsByLabel[COMMENT_LABELS.CLOSED]?.commentValue?.[headerLanguage]}
                  />
                </div>
              )}
              {!data?.actionLedToNonSuccessfulStatus && (
                isCampaignRDMP(campaignInfo.creationUser.department) && !isVANCR
                  ? (
                    <div className={styles.OneColumns}>
                      <InputText
                        disabled
                        id="input-status-comment-for-approvers"
                        label={t('recipients.commentsRdCampaignManager')}
                        multiline
                        rows={4}
                        value={commentsByLabel[COMMENT_LABELS.IN_RD_COMMITTEE]?.commentValue?.[headerLanguage]}
                      />
                    </div>
                  )
                  : (
                    <>
                      {displayComment(APPROVER_CR) && !isInformer
                        && (
                          <div className={styles.OneColumns}>
                            <InputText
                              disabled
                              id="input-status-comment-for-approvers"
                              label={t('dialog.statusCommentForApprovers')}
                              multiline
                              rows={4}
                              value={commentsByLabel[COMMENT_LABELS.IN_CAUSER_APPROVAL]?.commentValue?.[headerLanguage]}
                            />
                          </div>
                        )}
                      {displayComment(APPROVER_CR) && isInformer && !isVANCR && (
                        <div className={styles.OneColumns}>
                          <InputText
                            disabled
                            id="input-status-comments-for-additional-recipients"
                            label={t('dialog.StatusCommentsForAdditionalRecipients')}
                            multiline
                            rows={4}
                            value={commentsByLabel[COMMENT_LABELS.IN_INFORMER_APPROVAL]?.commentValue?.[headerLanguage]}
                          />
                        </div>
                      )}
                    </>
                  )
              )}
            </>
          )}
          {data?.statusCode !== 'IN_CREATION' && (
            <>
              <div className={styles.Flex}>
                <div className={styles.TwoColumns}>
                  <InputText
                    disabled
                    id="input-status-decision"
                    label={t('dialog.statusDecision')}
                    value={getActionTakenLabel(data?.actionTaken, data?.statusCode)}
                  />
                </div>
                <div className={styles.TwoColumns}>
                  <InputText
                    disabled
                    id="input-status-version"
                    label={t('dialog.statusVersion')}
                    value={data?.version}
                  />
                </div>
                <div className={styles.TwoColumns}>
                  <InputText
                    disabled
                    id="input-status-standIn"
                    label={t('dialog.statusTimestamp')}
                    value={format(new Date(data?.actionDate), DAY_DATE_FORMAT)}
                  />
                </div>
              </div>
              {(!isVANCR || (isVANCR && statusCampaign?.IN_REVIEW === data?.statusCode)) && (
                <div className={styles.OneColumns}>
                  <InputText
                    disabled
                    id="input-status-comments-e2"
                    label={t('dialog.statusComment')}
                    multiline
                    rows={4}
                    value={data?.comments[0]?.commentValue[headerLanguage]}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={close}>{t('dialog.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

const mapStateToProps = (state) => ({
  campaignInfo: getCampaignInfo(state),
});

export default connect(mapStateToProps)(DialogStatus);
